import type React from 'react'
import { createContext, useContext, useMemo } from 'react'

import { AVATAR_PALETTE, getAvatarColor } from './avatar'

type Participant = { userId: string; name: string }

const ParticipantPaletteContext = createContext<Map<string, string> | null>(null)

interface ParticipantPaletteProviderProps {
  participants: Participant[]
  children: React.ReactNode
}

/**
 * Assigns each participant in one basket a distinct avatar colour. Hashing a single key can't
 * do this — two userIds collide into the same palette slot for ~20% of three-person baskets —
 * so colours are dealt out by position over the whole roster instead, and shared via context
 * so the strip and the per-line attribution rows still agree. Wraps past AVATAR_PALETTE.length;
 * the strip collapses to a "+N" chip well before then, so repeats stay off-screen.
 */
export const ParticipantPaletteProvider: React.FC<ParticipantPaletteProviderProps> = ({
  participants,
  children,
}) => {
  const colorByUserId = useMemo(() => {
    const map = new Map<string, string>()
    participants.forEach((participant) => {
      if (map.has(participant.userId)) return
      map.set(participant.userId, AVATAR_PALETTE[map.size % AVATAR_PALETTE.length])
    })
    return map
  }, [participants])

  return (
    <ParticipantPaletteContext.Provider value={colorByUserId}>
      {children}
    </ParticipantPaletteContext.Provider>
  )
}

/**
 * Colour for one participant. Falls back to the hashed colour — which can collide — when there's
 * no provider, or when the key isn't on the roster (an attributed line whose author has since
 * been removed), so an avatar is never left unpainted.
 */
export const useParticipantColor = (key: string): string => {
  const colorByUserId = useContext(ParticipantPaletteContext)
  return colorByUserId?.get(key) ?? getAvatarColor(key)
}
